import React, { useState, useEffect, useRef, useMemo } from 'react';

const AnniversarySurprise = () => {
  // Using useMemo to prevent the arrays from being recreated on each render
  const images = useMemo(() => [
    '/images/IMG_7649.jpeg',
    '/images/IMG_7750.jpeg',
    '/images/IMG_8381.jpeg',
    '/images/IMG_8426.jpeg',
    '/images/IMG_8460.jpeg',
  ], []);
  
  // Using useMemo for messages array as well
  const messages = useMemo(() => [
      "Happy One Month Anniversary, My Love…",
      "30 days filled with laughter, smiles, and a love that grows stronger every moment…",
      "From best friends to soulmates… what a beautiful journey we've begun.",
      "Thank you for choosing me — not just as a partner, but as your person.",
      "I know my actions haven't always been perfect…",
      "And for the times I've hurt you — I am deeply sorry.",
      "But I believe love is more than just saying 'sorry'…",
      "I promise — I will be better for you, for us.",
      "I'm ready to walk through this life, hand in hand, heart to heart…",
      "Because loving you is the best part of my every day.",
      "Here's to us, and the endless chapters we've yet to write together.",
      "Love You 3000"
  ], []);
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const audioRef = useRef(null);
  
  // Background image cycling effect
  useEffect(() => {
    if (!isStarted) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [images.length, isStarted]);
  
  // Typing effect
  useEffect(() => {
    if (!isStarted) return;
    if (charIndex >= messages[messageIndex]?.length) {
      // Current message is complete
      const messageDelay = setTimeout(() => {
        if (messageIndex < messages.length - 1) {
          // Move to the next message
          setMessageIndex(messageIndex + 1);
          setCharIndex(0);
          setDisplayText('');
          // Stop audio when message is complete
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        } else {
          setIsTypingComplete(true);
          // Stop audio when all messages are complete
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        }
      }, 2000);
      
      return () => clearTimeout(messageDelay);
    }
    
    // Add next character
    const typingTimer = setTimeout(() => {
      setDisplayText(prevText => prevText + messages[messageIndex][charIndex]);
      setCharIndex(charIndex + 1);
      
      // Play audio only if it's not already playing
      if (audioRef.current && audioRef.current.paused) {
        audioRef.current.play().catch(e => console.log("Audio play error:", e));
      }
    }, 100);
    
    return () => clearTimeout(typingTimer);
  }, [charIndex, messageIndex, messages, isStarted]);
  
  // Floating hearts effect
  const HeartAnimation = () => {
    // Fewer hearts on mobile
    const heartCount = window.innerWidth < 768 ? 8 : 15;
    const hearts = [];
    
    for (let i = 0; i < heartCount; i++) {
      // Smaller hearts for mobile
      const size = window.innerWidth < 768 ? 16 + Math.random() * 12 : 20 + Math.random() * 16;
      
      hearts.push(
        <div
          key={i}
          className="absolute animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDuration: `${5 + Math.random() * 10}s`,
            animationDelay: `${Math.random() * 5}s`,
            opacity: 0.7,
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="red" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
          </svg>
        </div>
      );
    }
    return <>{hearts}</>;
  };

  const handleStart = () => {
    setIsStarted(true);
    // Preload audio
    if (audioRef.current) {
      audioRef.current.load();
    }
  };
  
  return (
    <div className="relative h-screen w-full overflow-hidden bg-black">
      {/* Background images with fade effect */}
      {images.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 bg-cover bg-center ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ backgroundImage: `url(${img})` }}
        />
      ))}
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      {/* Floating hearts */}
      {isTypingComplete && <HeartAnimation />}
      
      {/* Main content container */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 md:p-8">
        {/* Audio element for typing sound effect */}
        <audio 
          ref={audioRef} 
          src="/audio/short-audio.mov"
          preload="auto"
          onError={(e) => console.log("Audio loading error:", e)}
        />
        
        {!isStarted ? (
          <button 
            onClick={handleStart} 
            className="bg-white text-black px-4 py-2 rounded z-10"
          >
            Click to begin
          </button>
        ) : (
          <>
            {/* Typing text */}
            <div className="text-center w-full">
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6">{displayText}</h1>
              <div className="typing-cursor inline-block w-1 h-6 md:h-8 bg-white animate-blink ml-1" 
                  style={{ visibility: isTypingComplete ? 'hidden' : 'visible' }}></div>
            </div>
            
            {/* Additional content that appears after typing is complete */}
            {isTypingComplete && (
              <div className="mt-8 md:mt-12 transition-opacity duration-1000 opacity-100 text-center">
                <p className="text-xl md:text-2xl mb-4 md:mb-6">Our journey together has been amazing!</p>
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="red" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 md:mb-6 animate-pulse">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                </svg>
                <p className="text-base md:text-lg">I can't wait to create more memories with you!</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnniversarySurprise;